import i18next, { type BackendModule, type ReadCallback } from "i18next";
import { initReactI18next } from "react-i18next";

/** Idiomas del portal (los mismos del portal actual). */
export const locales = ["es-PE", "en-US"] as const;
export type Locale = (typeof locales)[number];

/**
 * Grupos de textos por pantalla: cada «namespace» de i18next es una lista de prefijos de clave del diccionario de
 * Platform (las mismas claves que el portal actual). Así cada pantalla pide solo lo suyo.
 */
export const textGroups: Record<string, string[]> = {
  login: ["LOGIN_", "CP_AUTH_", "CP_LOGIN_"],
  shell: ["CP_SHELL_", "CP_NAV_", "SHELL_", "NAV_", "CP_MENU_"],
  tags: ["C1_", "CTAG_", "TAG_", "CT_", "CONTACT_TAG"],
};

/** Texto que se muestra cuando una clave no existe: igual que PortalText del portal actual. */
export const missingText = (key: string): string => `⟦${key}⟧`;

/** Pide al servidor del portal los textos de un idioma y un grupo (no hay textos escritos en el cliente). */
const portalBackend: BackendModule = {
  type: "backend",
  init: () => undefined,
  read(language: string, namespace: string, callback: ReadCallback) {
    const prefixes = textGroups[namespace] ?? [];
    const query = prefixes.length ? `?prefixes=${encodeURIComponent(prefixes.join(","))}` : "";
    fetch(`/api/portal/texts/${encodeURIComponent(language)}${query}`, { credentials: "same-origin" })
      .then(async (response) => {
        if (!response.ok) throw new Error(`textos ${response.status}`);
        callback(null, (await response.json()) as Record<string, string>);
      })
      .catch((error: unknown) => callback(error as Error, null));
  },
};

/** Inicia i18next con el idioma de la persona (cookie de ayuda bheli-locale, como el portal actual). */
export async function initTexts(locale: Locale, groups: string[]): Promise<typeof i18next> {
  await i18next
    .use(portalBackend)
    .use(initReactI18next)
    .init({
      lng: locale,
      fallbackLng: false,
      ns: groups,
      defaultNS: groups[0],
      // Las claves son planas (CHAT_R2_ASSIGN): sin separadores de anidado ni de namespace.
      keySeparator: false,
      nsSeparator: false,
      interpolation: { escapeValue: false },
      returnNull: false,
      parseMissingKeyHandler: missingText,
      react: { useSuspense: false },
    });
  return i18next;
}

/** Idioma de la cookie de ayuda o es-PE. */
export function localeFromCookie(cookie: string = document.cookie): Locale {
  const value = cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("bheli-locale="))
    ?.slice("bheli-locale=".length);
  return value === "en-US" ? "en-US" : "es-PE";
}

/** Reemplaza {0}, {1}… como string.Format en los textos del portal actual. */
export function formatText(template: string, ...values: (string | number)[]): string {
  return template.replace(/\{(\d+)\}/g, (match, index: string) => {
    const value = values[Number(index)];
    return value === undefined ? match : String(value);
  });
}
